import { useState } from 'react';
import { Search, SlidersHorizontal, X, Grid3X3, List } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';
import { Input } from './ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetFooter } from './ui/sheet';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { useIsMobile } from '@/hooks/use-mobile';

const goalFilters = [
  { value: 'travel', label: 'Reise', emoji: '✈️' },
  { value: 'no-fee', label: 'Keine Jahresgebühr', emoji: '🟢' },
  { value: 'no-foreign-fee', label: '0% Ausland', emoji: '🌍' },
  { value: 'visa', label: 'Visa', emoji: '' },
  { value: 'mastercard', label: 'Mastercard', emoji: '' },
  { value: 'amex', label: 'Amex', emoji: '' },
  { value: 'prepaid', label: 'Prepaid', emoji: '💳' },
  { value: 'with-account', label: 'Mit Girokonto', emoji: '🏦' },
  { value: 'discounts', label: 'Rabatte', emoji: '🎁' },
  { value: 'hotline', label: '24/7 Hotline', emoji: '📞' },
];

const sortLabels: Record<string, string> = {
  recommended: 'Empfohlen',
  'low-fee': 'Jahresgebühr ↑',
  'low-apr': 'Zinssatz ↑',
  'benefits': 'Vorteile ↓',
};

const filterLabels: Record<string, string> = {
  'travel': 'Reise',
  'cashback-bonus': 'Cashback / Bonus',
  'no-fee': 'Keine Jahresgebühr',
  'nfc': 'NFC',
  'no-foreign-fee': '0% Ausland',
  'visa': 'Visa',
  'mastercard': 'Mastercard',
  'amex': 'Amex',
  'prepaid': 'Prepaid',
  'with-account': 'Mit Girokonto',
  'discounts': 'Rabatte',
  'hotline': '24/7 Hotline',
};

interface ModernFilterProps {
  searchQuery: string;
  onSearchChange: (value: string) => void;
  selectedFilters: string[];
  onFilterToggle: (filter: string) => void;
  sortBy: string;
  onSortChange: (value: string) => void;
  viewMode: 'grid' | 'list';
  onViewModeChange: (mode: 'grid' | 'list') => void;
  
  // Advanced filters
  advancedFilters: {
    cardTypes: string[];
    interestMax: number | null;
    foreignMax: number | null;
    issuers: string[];
    nfc: boolean;
  };
  onAdvancedFiltersChange: (filters: ModernFilterProps['advancedFilters']) => void;
  availableIssuers: string[];
  
  // Data-aware props
  chipCounts: Record<string, number>;
  cardTypeCoverage: {
    credit: boolean;
    charge: boolean;
    debit: boolean;
  };
  
  // Results info
  visibleCount: number;
  totalCount: number;
  
  // Callbacks
  onResetAll: () => void;
}

export function ModernFilter({
  searchQuery,
  onSearchChange,
  selectedFilters,
  onFilterToggle,
  sortBy,
  onSortChange,
  viewMode,
  onViewModeChange,
  advancedFilters,
  onAdvancedFiltersChange,
  availableIssuers,
  chipCounts,
  cardTypeCoverage,
  visibleCount,
  totalCount,
  onResetAll,
}: ModernFilterProps) {
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const isMobile = useIsMobile();

  const activeAdvancedFiltersCount = [
    advancedFilters.cardTypes.length > 0,
    advancedFilters.interestMax !== null,
    advancedFilters.foreignMax !== null,
    advancedFilters.issuers.length > 0,
    advancedFilters.nfc,
  ].filter(Boolean).length;

  const hasActiveFilters = 
    selectedFilters.length > 0 || 
    searchQuery.length > 0 || 
    activeAdvancedFiltersCount > 0;

  const getSummaryText = () => {
    if (searchQuery) {
      return `Suche: "${searchQuery}"`;
    }
    
    if (selectedFilters.length > 0) {
      const filterNames = selectedFilters.map(f => filterLabels[f] || f).join(', ');
      return `Filter: ${filterNames}`;
    }
    
    return 'Alle Kreditkarten';
  };

  const summaryText = getSummaryText();

  return (
    <div className="space-y-0" data-filters>
      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        
        {/* Top Bar: Title + View Toggle */}
        <div className="px-6 pt-6 pb-3 flex items-center justify-between">
          <h3 className="text-xl font-semibold text-gray-900">Finden Sie Ihre Kreditkarte</h3>
          
          {/* View Toggle */}
          <div className="hidden sm:flex gap-1 border border-gray-200 rounded-lg p-1 bg-gray-50">
            <button
              onClick={() => onViewModeChange('grid')}
              title="Kartenvansicht"
              className={`p-2 rounded-sm transition-colors ${
                viewMode === 'grid'
                  ? 'bg-white text-blue-600 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              onClick={() => onViewModeChange('list')}
              title="Tabellenansicht"
              className={`p-2 rounded-sm transition-colors ${
                viewMode === 'list'
                  ? 'bg-white text-blue-600 shadow-xs'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Chips Row */}
        <div className="px-6 pb-4">
          <div className="overflow-x-auto scrollbar-hide -mx-6 px-6 sm:mx-0 sm:px-0">
            <div className="flex gap-2 min-w-max sm:min-w-0">
              {goalFilters.map((filter) => {
                const count = chipCounts[filter.value] || 0;
                if (count === 0) return null; // Hide chip with 0 count
                
                const isSelected = selectedFilters.includes(filter.value);
                
                const chipButton = (
                  <button
                    key={filter.value}
                    onClick={() => onFilterToggle(filter.value)}
                    className={`
                      inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm
                      transition-all duration-200 whitespace-nowrap
                      ${isSelected
                        ? 'bg-[#2563EB] text-white shadow-md hover:bg-[#1D4ED8]'
                        : 'bg-white border border-gray-300 text-gray-700 hover:border-gray-400 hover:bg-gray-50'
                      }
                    `}
                  >
                    {filter.emoji && <span className="text-base">{filter.emoji}</span>}
                    <span>{filter.label}</span>
                    {count > 0 && !isSelected && <span className="text-xs opacity-70">({count})</span>}
                    {isSelected && <X className="w-3.5 h-3.5 ml-0.5" />}
                  </button>
                );
                
                // Add tooltip only for "travel" chip
                if (filter.value === 'travel') {
                  return (
                    <Tooltip key={filter.value}>
                      <TooltipTrigger asChild>
                        {chipButton}
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Karten mit Reiseversicherung, Reisekrankenversicherung oder Reiserabatten</p>
                      </TooltipContent>
                    </Tooltip>
                  );
                }
                
                return chipButton;
              })}
            </div>
          </div>
        </div>

        {/* Search + Controls Bar */}
        <div className="px-6 py-4 bg-gray-50 border-y border-gray-100">
          <div className="flex gap-3 items-center flex-wrap sm:flex-nowrap">
            
            {/* Search */}
            <div className="flex-1 w-full sm:w-auto min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                type="text"
                placeholder="Suche nach Karte, Bank oder Vorteil…"
                value={searchQuery}
                onChange={(e) => onSearchChange(e.target.value)}
                className="pl-10 h-10 rounded-lg bg-white"
              />
            </div>

            {/* Sort */}
            <div className="hidden sm:flex items-center gap-2">
              <span className="text-sm text-gray-600 whitespace-nowrap">Sortieren:</span>
              <Select value={sortBy} onValueChange={onSortChange}>
                <SelectTrigger className="w-[140px] h-10 rounded-lg bg-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(sortLabels).map(([value, label]) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Filtrer Button - Hidden for now */}
            {false && (
              <Button
                onClick={() => setShowAdvancedFilters(true)}
                variant="outline"
                size="sm"
                className="h-10 gap-2 relative bg-white"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="hidden sm:inline">Weitere Filter</span>
                {activeAdvancedFiltersCount > 0 && (
                  <Badge className="absolute -top-1 -right-1 h-5 min-w-[20px] px-1 flex items-center justify-center bg-blue-600 text-white text-xs">
                    {activeAdvancedFiltersCount}
                  </Badge>
                )}
              </Button>
            )}
          </div>
        </div>

        {/* Results Header - Integrated */}
        <div className="px-6 py-6 space-y-4">
          
          {/* Title + Count */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h4 className="text-gray-900">Kreditkarten für Sie</h4>
            <p className="text-sm text-gray-600">
              Zeige <strong className="text-gray-900">{visibleCount}</strong> von{' '}
              <strong className="text-gray-900">{totalCount}</strong> Karten
            </p>
          </div>

          {/* Summary */}
          {summaryText && (
            <div className="flex flex-col sm:flex-row sm:items-start gap-3 p-4 rounded-lg bg-[#F9FAFB]">
              <div className="hidden sm:flex w-8 h-8 items-center justify-center flex-shrink-0 bg-gray-100 rounded-full">
                <span className="text-lg">💡</span>
              </div>
              <p className="text-sm text-gray-700 sm:pt-1 leading-relaxed flex-1 sm:pr-4">{summaryText}</p>
              {hasActiveFilters && (
                <button
                  onClick={onResetAll}
                  className="text-sm text-blue-600 hover:text-blue-700 hover:underline whitespace-nowrap self-start sm:flex-shrink-0 sm:pt-1"
                >
                  Zurücksetzen
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Advanced Filters Modal/Sheet */}
      {isMobile ? (
        <Sheet open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
          <SheetContent side="bottom" className="h-[80vh] p-0 flex flex-col">
            <SheetHeader className="px-4 pt-6 pb-4">
              <SheetTitle className="text-2xl font-bold text-left">Weitere Filter</SheetTitle>
            </SheetHeader>
            <div className="px-4 py-2 space-y-6 overflow-y-auto flex-1">
            <AdvancedFiltersContent
                cardTypes={advancedFilters.cardTypes}
                onCardTypesChange={(types) => 
                  onAdvancedFiltersChange({ ...advancedFilters, cardTypes: types })
                }
                interestMax={advancedFilters.interestMax}
                onInterestMaxChange={(val) => 
                  onAdvancedFiltersChange({ ...advancedFilters, interestMax: val })
                }
                foreignMax={advancedFilters.foreignMax}
                onForeignMaxChange={(val) => 
                  onAdvancedFiltersChange({ ...advancedFilters, foreignMax: val })
                }
                issuers={availableIssuers}
                selectedIssuers={advancedFilters.issuers}
                onIssuersChange={(issuers) => 
                  onAdvancedFiltersChange({ ...advancedFilters, issuers })
                }
                cardTypeCoverage={cardTypeCoverage}
                nfcEnabled={advancedFilters.nfc}
                onNfcChange={(enabled) =>
                  onAdvancedFiltersChange({ ...advancedFilters, nfc: enabled })
                }
              />
            </div>
            <SheetFooter className="flex-row gap-3 px-4 py-4 border-t bg-white mt-auto">
              <Button 
                variant="outline" 
                onClick={onResetAll} 
                className="flex-1 h-12 text-base"
              >
                Zurücksetzen
              </Button>
              <Button 
                onClick={() => setShowAdvancedFilters(false)} 
                className="flex-1 h-12 text-base bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                Zeige {visibleCount} Karten →
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={showAdvancedFilters} onOpenChange={setShowAdvancedFilters}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Weitere Filter</DialogTitle>
            </DialogHeader>
            <AdvancedFiltersContent
              cardTypes={advancedFilters.cardTypes}
              onCardTypesChange={(types) => 
                onAdvancedFiltersChange({ ...advancedFilters, cardTypes: types })
              }
              interestMax={advancedFilters.interestMax}
              onInterestMaxChange={(val) => 
                onAdvancedFiltersChange({ ...advancedFilters, interestMax: val })
              }
              foreignMax={advancedFilters.foreignMax}
              onForeignMaxChange={(val) => 
                onAdvancedFiltersChange({ ...advancedFilters, foreignMax: val })
              }
              issuers={availableIssuers}
              selectedIssuers={advancedFilters.issuers}
              onIssuersChange={(issuers) => 
                onAdvancedFiltersChange({ ...advancedFilters, issuers })
              }
              cardTypeCoverage={cardTypeCoverage}
              nfcEnabled={advancedFilters.nfc}
              onNfcChange={(enabled) =>
                onAdvancedFiltersChange({ ...advancedFilters, nfc: enabled })
              }
            />
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                onClick={onResetAll}
                className="flex-1"
              >
                Zurücksetzen
              </Button>
              <Button
                onClick={() => setShowAdvancedFilters(false)}
                className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8]"
              >
                Zeige {visibleCount} Karten
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface AdvancedFiltersContentProps {
  cardTypes: string[];
  onCardTypesChange: (types: string[]) => void;
  interestMax: number | null;
  onInterestMaxChange: (value: number | null) => void;
  foreignMax: number | null;
  onForeignMaxChange: (value: number | null) => void;
  issuers: string[];
  selectedIssuers: string[];
  onIssuersChange: (issuers: string[]) => void;
  cardTypeCoverage: {
    credit: boolean;
    charge: boolean;
    debit: boolean;
  };
  nfcEnabled: boolean;
  onNfcChange: (enabled: boolean) => void;
}

function AdvancedFiltersContent({
  cardTypes,
  onCardTypesChange,
  interestMax,
  onInterestMaxChange,
  foreignMax,
  onForeignMaxChange,
  issuers,
  selectedIssuers,
  onIssuersChange,
  cardTypeCoverage,
  nfcEnabled,
  onNfcChange,
}: AdvancedFiltersContentProps) {
  return (
    <div className="space-y-6 py-4">
      <div>
        <Label className="text-base font-semibold mb-3 block">Kartentyp</Label>
        <div className="space-y-2">
          {[
            { value: 'credit', label: 'Kreditkarte' },
            { value: 'charge', label: 'Charge-Karte' },
            { value: 'debit', label: 'Debitkarte' },
          ].map(type => (
            <div key={type.value} className="flex items-center space-x-2">
              <Checkbox
                id={type.value}
                checked={cardTypes.includes(type.value)}
                disabled={!cardTypeCoverage[type.value as keyof typeof cardTypeCoverage]}
                onCheckedChange={(checked) => {
                  if (checked) {
                    onCardTypesChange([...cardTypes, type.value]);
                  } else {
                    onCardTypesChange(cardTypes.filter(t => t !== type.value));
                  }
                }}
              />
              <Label 
                htmlFor={type.value} 
                className={`cursor-pointer ${!cardTypeCoverage[type.value as keyof typeof cardTypeCoverage] ? 'opacity-50' : ''}`}
              >
                {type.label}
              </Label>
            </div>
          ))}
        </div>
      </div>

      <div>
        <Label className="text-base font-semibold mb-3 block">Weitere Eigenschaften</Label>
        <div className="space-y-2">
          <div className="flex items-center space-x-2">
            <Checkbox
              id="nfc"
              checked={nfcEnabled}
              onCheckedChange={(checked) => onNfcChange(!!checked)}
            />
            <Label htmlFor="nfc" className="cursor-pointer">📶 NFC / Kontaktlos</Label>
          </div>
        </div>
      </div>

      <div>
        <Label className="text-base font-semibold mb-3 block">
          Max. Effektivzins (%)
        </Label>
        <Input
          type="number"
          min="0"
          max="50"
          step="0.5"
          value={interestMax ?? ''}
          onChange={(e) => {
            const val = e.target.value ? parseFloat(e.target.value) : null;
            onInterestMaxChange(val);
          }}
          placeholder="z.B. 15"
        />
      </div>

      <div>
        <Label className="text-base font-semibold mb-3 block">
          Max. Ausland (%)
        </Label>
        <Input
          type="number"
          min="0"
          max="5"
          step="0.1"
          value={foreignMax ?? ''}
          onChange={(e) => {
            const val = e.target.value ? parseFloat(e.target.value) : null;
            onForeignMaxChange(val);
          }}
          placeholder="z.B. 1.5"
        />
      </div>

      <div>
        <Label className="text-base font-semibold mb-3 block">Herausgeber</Label>
        <Select
          value=""
          onValueChange={(value) => {
            if (value && !selectedIssuers.includes(value)) {
              onIssuersChange([...selectedIssuers, value]);
            }
          }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Wählen Sie eine Bank..." />
          </SelectTrigger>
          <SelectContent>
            {issuers.map(issuer => (
              <SelectItem key={issuer} value={issuer}>
                {issuer}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        
        {selectedIssuers.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedIssuers.map(issuer => (
              <Badge key={issuer} variant="secondary" className="gap-1">
                {issuer}
                <X 
                  className="w-3 h-3 cursor-pointer" 
                  onClick={() => onIssuersChange(selectedIssuers.filter(i => i !== issuer))}
                />
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
